/**
 * ClickableWord Component
 * Makes English words clickable to show dictionary popover
 * Supports CEFR level highlighting
 */

import React, { useState } from 'react';
import { DictionaryPopover } from './DictionaryPopover';
import { findInCefrMap } from '../utils/lemmatizer';
import { splitPossessive } from '../utils/wordSplit';
import './ClickableWord.css';

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

interface ClickableWordProps {
  word: string;
  className?: string;
  cefrLevel?: CEFRLevel;
  isPhrase?: boolean;
  highlighted?: boolean;
}

export const ClickableWord: React.FC<ClickableWordProps> = ({ word, className = '', cefrLevel, isPhrase, highlighted }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate popover position
    let x = rect.left;
    let y = rect.bottom + 8; // 8px below the word

    // Adjust horizontal position if too close to right edge
    if (x + 400 > viewportWidth) {
      x = viewportWidth - 420; // 400px popover width + 20px margin
    }

    // Adjust vertical position if too close to bottom edge
    if (y + 300 > viewportHeight) {
      y = rect.top - 308; // Show above the word instead
    }

    setPopoverPosition({ x, y });
    setShowPopover(true);
  };

  const cefrClass = cefrLevel ? `clickable-word--cefr clickable-word--cefr-${cefrLevel}` : '';
  const phraseClass = isPhrase ? 'clickable-word--phrase' : '';
  const highlightClass = highlighted ? 'clickable-word--highlighted' : '';

  return (
    <>
      <span
        className={`clickable-word ${cefrClass} ${phraseClass} ${highlightClass} ${className}`}
        onClick={handleClick}
        title={cefrLevel ? `CEFR ${cefrLevel} - Click to see definition` : 'Click to see definition'}
        data-cefr={cefrLevel}
      >
        {word}
        {cefrLevel && <span className="clickable-word__cefr-indicator">{cefrLevel}</span>}
      </span>

      {showPopover && (
        <DictionaryPopover
          word={word}
          position={popoverPosition}
          onClose={() => setShowPopover(false)}
        />
      )}
    </>
  );
};

/**
 * ClickablePhrase Component
 * Renders a multi-word phrase with CEFR highlighting
 */
interface ClickablePhraseProps {
  phrase: string;
  cefrLevel: CEFRLevel;
}

export const ClickablePhrase: React.FC<ClickablePhraseProps> = ({ phrase, cefrLevel }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

  const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = rect.left;
    let y = rect.bottom + 8;

    if (x + 400 > viewportWidth) {
      x = viewportWidth - 420;
    }

    if (y + 300 > viewportHeight) {
      y = rect.top - 308;
    }

    setPopoverPosition({ x, y });
    setShowPopover(true);
  };

  return (
    <>
      <span
        className={`clickable-word clickable-word--cefr clickable-word--cefr-${cefrLevel} clickable-word--phrase`}
        onClick={handleClick}
        title={`CEFR ${cefrLevel} - Phrasal Verb`}
        data-cefr={cefrLevel}
      >
        {phrase}
        <span className="clickable-word__cefr-indicator">{cefrLevel}</span>
      </span>

      {showPopover && (
        <DictionaryPopover
          word={phrase}
          position={popoverPosition}
          onClose={() => setShowPopover(false)}
        />
      )}
    </>
  );
};

/**
 * ClickableText Component
 * Automatically detects and makes English words clickable in a text block
 * Supports CEFR level highlighting for both single words and multi-word phrases
 */
interface ClickableTextProps {
  text: string;
  className?: string;
  cefrLevelMap?: Record<string, CEFRLevel>;
  highlightWords?: string[];
}

interface TextSegment {
  type: 'text' | 'word' | 'phrase';
  content: string;
  cefrLevel?: CEFRLevel;
  startIndex: number;
  endIndex: number;
}

export const ClickableText: React.FC<ClickableTextProps> = ({ text, className = '', cefrLevelMap, highlightWords }) => {
  // Handle null/undefined text
  if (!text || text.length === 0) {
    return <span className={className}></span>;
  }

  // Build a Set of lowercased highlight words for efficient lookup
  const highlightSet = highlightWords && highlightWords.length > 0
    ? new Set(highlightWords.map(w => w.toLowerCase()))
    : undefined;

  const renderText = () => {
    if (!cefrLevelMap) {
      // No CEFR map - just render clickable words
      return renderSimpleClickableText(text, 0, undefined, highlightSet);
    }

    // Find all multi-word phrases (phrasal verbs) in the cefrLevelMap
    const phrases = Object.keys(cefrLevelMap).filter(term => term.includes(' '));

    // Sort phrases by length (longest first) to match longer phrases first
    phrases.sort((a, b) => b.length - a.length);

    // Find phrase matches in the text
    const segments: TextSegment[] = [];
    const textLower = text.toLowerCase();
    const usedRanges: Array<{ start: number; end: number }> = [];

    // First pass: find all phrase matches
    for (const phrase of phrases) {
      let searchStart = 0;
      while (true) {
        const index = textLower.indexOf(phrase, searchStart);
        if (index === -1) break;

        const endIndex = index + phrase.length;

        // Check if this range overlaps with any existing range
        const overlaps = usedRanges.some(
          range => !(endIndex <= range.start || index >= range.end)
        );

        if (!overlaps) {
          // Check word boundaries
          const beforeChar = index > 0 ? text[index - 1] : ' ';
          const afterChar = endIndex < text.length ? text[endIndex] : ' ';
          const isWordBoundaryBefore = !/[a-zA-Z]/.test(beforeChar);
          const isWordBoundaryAfter = !/[a-zA-Z]/.test(afterChar);

          if (isWordBoundaryBefore && isWordBoundaryAfter) {
            segments.push({
              type: 'phrase',
              content: text.substring(index, endIndex),
              cefrLevel: cefrLevelMap[phrase],
              startIndex: index,
              endIndex: endIndex,
            });
            usedRanges.push({ start: index, end: endIndex });
          }
        }

        searchStart = index + 1;
      }
    }

    // Sort segments by start index
    segments.sort((a, b) => a.startIndex - b.startIndex);

    // Build the final render array
    const parts: React.ReactNode[] = [];
    let currentIndex = 0;

    for (const segment of segments) {
      // Add text/words before this segment
      if (segment.startIndex > currentIndex) {
        const beforeText = text.substring(currentIndex, segment.startIndex);
        parts.push(...renderSimpleClickableText(beforeText, currentIndex, cefrLevelMap, highlightSet));
      }

      // Add the phrase segment
      parts.push(
        <ClickablePhrase
          key={`phrase-${segment.startIndex}`}
          phrase={segment.content}
          cefrLevel={segment.cefrLevel ?? 'B1'}
        />
      );

      currentIndex = segment.endIndex;
    }

    // Add remaining text/words after the last segment
    if (currentIndex < text.length) {
      const remainingText = text.substring(currentIndex);
      parts.push(...renderSimpleClickableText(remainingText, currentIndex, cefrLevelMap, highlightSet));
    }

    return parts;
  };

  return <span className={className}>{renderText()}</span>;
};

/**
 * Helper function to render text with clickable single words
 */
function renderSimpleClickableText(
  text: string,
  startOffset = 0,
  cefrLevelMap?: Record<string, CEFRLevel>,
  highlightSet?: Set<string>,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const wordRegex = /\b[a-zA-Z]+(?:['-][a-zA-Z]+)*\b/g;
  let lastIndex = 0;
  let match;

  while ((match = wordRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Separate possessive "'s" off the noun so the dictionary lookup targets the base word.
    // (Pronoun contractions like "it's", "he's" stay intact — they're real dictionary entries.)
    const { base, tail } = splitPossessive(match[0]);
    const cefrLevel = cefrLevelMap ? findInCefrMap(base, cefrLevelMap) : undefined;
    const isHighlighted = highlightSet ? highlightSet.has(base.toLowerCase()) : false;
    parts.push(
      <ClickableWord
        key={`word-${startOffset + match.index}`}
        word={base}
        cefrLevel={cefrLevel}
        highlighted={isHighlighted}
      />
    );
    if (tail) {
      parts.push(
        <span key={`tail-${startOffset + match.index}`} className="clickable-word__possessive">
          {tail}
        </span>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}
